import { KeyboardArrowDown } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'

const CustomMultiSelect = ({
  label,
  value = [],
  onChange,
  options = [],
  getOptionLabel = option => option,
  getOptionValue = option => option,
  placeholder = 'Select options',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  fullWidth = false,
  ...props
}) => {
  const allValues = options.map(opt => getOptionValue(opt))
  const allSelected = value.length === allValues.length && allValues.length > 0

  // Unified handler for both SelectAll + single options
  const handleChange = e => {
    const selected = e.target.value

    // If "ALL" was clicked → toggle all
    if (selected.includes('ALL')) {
      onChange({
        target: {
          value: allSelected ? [] : allValues,
        },
      })
      return
    }

    // Normal update
    onChange({
      target: {
        value: selected,
      },
    })
  }

  return (
    <Box
      sx={{
        minWidth: fullWidth ? '100%' : 225,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            mb: 1,
            lineHeight: '140%',
          }}
        >
          {label}
          {required && (
            <span style={{ color: 'var(--forbrand-error)' }}>*</span>
          )}
        </Typography>
      )}
      <FormControl fullWidth size="small" disabled={disabled} error={error}>
        <Select
          multiple
          value={value || []}
          onChange={handleChange}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          renderValue={selected => {
            if (!selected || selected.length === 0) {
              return <span style={{ color: '#999' }}>{placeholder}</span>
            }
            const selectedLabels = options
              .filter(opt => selected.includes(getOptionValue(opt)))
              .map(opt => getOptionLabel(opt))
            const displayText = selectedLabels.join(', ')
            return (
              <Tooltip
                title={displayText}
                slotProps={{
                  tooltip: {
                    sx: {
                      maxHeight: '200px',
                      overflowY: 'auto',
                      scrollbarWidth: 'none', // Firefox
                      '&::-webkit-scrollbar': {
                        // Chrome, Safari, Edge
                        display: 'none',
                      },
                    },
                  },
                }}
                arrow
              >
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayText}
                </span>
              </Tooltip>
            )
          }}
          sx={{
            backgroundColor: disabled ? 'var(--bg-disabled)' : '#fff',
            fontSize: '14px',
            height: '48px',
            width: fullWidth ? '100%' : '225px',
            borderRadius: 'unset',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: error
                ? 'var(--forbrand-error)'
                : 'var(--forbrand-color)',
            },
            '& .MuiSelect-select': {
              fontSize: '14px',
              color: '#374151',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .MuiSelect-icon': { color: '#6b7280', fontSize: '20px' },
            '&.Mui-disabled': { backgroundColor: 'var(--bg-disabled)' },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                boxShadow:
                  '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                border: '1px solid var(--border-strong)',
                mt: 0.5,
                maxHeight: '250px',
                width: '225px',
              },
            },
          }}
          {...props}
        >
          {/* Select All */}
          <MenuItem value="ALL">
            <Checkbox
              sx={{
                color: 'var(--forbrand-color)',
                '&.Mui-checked': { color: 'var(--forbrand-color)' },
                '&.MuiCheckbox-indeterminate': {
                  color: 'var(--forbrand-color)',
                },
              }}
              checked={allSelected}
              indeterminate={value.length > 0 && value.length < options.length}
            />
            <ListItemText
              primary="Select All"
              sx={{
                '.MuiTypography-root': {
                  fontSize: '14px',
                },
              }}
            />
          </MenuItem>

          {/* All Other Options */}
          {options.map((option, index) => (
            <MenuItem
              key={index}
              value={getOptionValue(option)}
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              <Checkbox
                sx={{
                  color: 'var(--forbrand-color)',
                  '&.Mui-checked': { color: 'var(--forbrand-color)' },
                }}
                checked={value.includes(getOptionValue(option))}
              />
              <ListItemText
                primary={getOptionLabel(option)}
                sx={{
                  '.MuiTypography-root': {
                    fontSize: '14px',
                  },
                }}
              />
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? 'var(--forbrand-error)' : '#666',
              mt: 0.5,
              fontSize: '0.75rem',
            }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  )
}

export default CustomMultiSelect
